import type { ReactNode } from "react"
import { ArrowRight } from "lucide-react"

export type ApproachKind = "pre" | "post"

interface ApproachCardProps {
  kind: ApproachKind
  tag: string
  tagSub?: ReactNode
  title: string
  body: ReactNode
  bullets: string[]
  cta?: { label: string; href?: string; onClick?: () => void }
  recommended?: boolean
}

export function ApproachCard({
  kind,
  tag,
  tagSub,
  title,
  body,
  bullets,
  cta,
  recommended,
}: ApproachCardProps) {
  const isPre = kind === "pre"
  const accentText = isPre ? "text-green-700" : "text-amber-600"
  const accentBg = isPre ? "bg-green-50" : "bg-amber-50"
  const accentDot = isPre ? "bg-green-600" : "bg-amber-500"
  const recommendedBg = isPre ? "bg-green-600" : "bg-amber-600"
  const borderClass = recommended
    ? isPre
      ? "border-green-600 shadow-lg shadow-green-100"
      : "border-amber-600 shadow-lg shadow-amber-100"
    : "border-gray-200"

  return (
    <div className={`relative rounded-2xl border-[1.5px] bg-white p-6 ${borderClass}`}>
      {recommended && (
        <div
          className={`absolute -top-2.5 left-6 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white ${recommendedBg}`}
        >
          Start here
        </div>
      )}
      <div
        className={`inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${accentBg} ${accentText}`}
      >
        <span className={`h-2 w-2 rounded-full ${accentDot}`} />
        {tag}
      </div>
      {tagSub && <div className="mt-2 text-xs text-gray-500">{tagSub}</div>}
      <h3 className="mt-2 text-xl font-bold tracking-tight text-gray-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-500">{body}</p>
      <ul className="mt-4 space-y-2">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-sm text-gray-900">
            <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${accentDot}`} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {cta && (
        <CtaLink kind={kind} {...cta} />
      )}
    </div>
  )
}

function CtaLink({
  kind,
  label,
  href,
  onClick,
}: {
  kind: ApproachKind
  label: string
  href?: string
  onClick?: () => void
}) {
  const accentText = kind === "pre" ? "text-green-700" : "text-amber-600"
  const className = `mt-4 inline-flex items-center gap-1.5 text-sm font-semibold ${accentText}`
  const inner = (
    <>
      {label} <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </>
  )
  if (href) {
    return (
      <a href={href} className={className}>
        {inner}
      </a>
    )
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  )
}

export interface EdgeCardStat {
  eyebrow: string
  big: ReactNode
  sub: string
  tone?: "green" | "amber" | "ink"
}

interface EdgeCardProps {
  tone: "ink" | "amber" | "muted"
  tag: string
  tagSub?: string
  headline: ReactNode
  body: ReactNode
  stats: EdgeCardStat[]
  primary?: { label: string; onClick?: () => void; href?: string }
  secondary?: { label: string; onClick?: () => void; href?: string }
}

export function EdgeCard({ tone, tag, tagSub, headline, body, stats, primary, secondary }: EdgeCardProps) {
  const accentText =
    tone === "amber" ? "text-amber-600" : tone === "muted" ? "text-gray-500" : "text-green-700"
  const accentBg =
    tone === "amber" ? "bg-amber-50" : tone === "muted" ? "bg-gray-100" : "bg-green-50"
  const accentDot =
    tone === "amber" ? "bg-amber-500" : tone === "muted" ? "bg-gray-400" : "bg-green-600"

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xs sm:p-7">
      <div
        className={`inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${accentBg} ${accentText}`}
      >
        <span className={`h-2 w-2 rounded-full ${accentDot}`} />
        {tag}
      </div>
      {tagSub && <div className="mt-2 text-xs text-gray-500">{tagSub}</div>}
      <h3 className="mt-2 text-balance text-2xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-3xl">
        {headline}
      </h3>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-gray-500 sm:text-base">{body}</p>

      <div className="mt-5 grid grid-cols-1 gap-5 rounded-xl bg-gray-50 p-5 sm:grid-cols-3">
        {stats.map((s, i) => {
          const c =
            s.tone === "amber" ? "text-amber-600" : s.tone === "green" ? "text-green-700" : "text-gray-900"
          return (
            <div key={i}>
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                {s.eyebrow}
              </div>
              <div className={`mt-1 text-2xl font-extrabold tabular-nums tracking-tight ${c}`}>
                {s.big}
              </div>
              <div className="mt-0.5 text-xs text-gray-500">{s.sub}</div>
            </div>
          )
        })}
      </div>

      {(primary || secondary) && (
        <div className="mt-5 flex flex-wrap gap-2.5">
          {primary && <EdgeButton variant="primary" {...primary} />}
          {secondary && <EdgeButton variant="secondary" {...secondary} />}
        </div>
      )}
    </div>
  )
}

function EdgeButton({
  variant,
  label,
  onClick,
  href,
}: {
  variant: "primary" | "secondary"
  label: string
  onClick?: () => void
  href?: string
}) {
  const className =
    variant === "primary"
      ? "inline-flex min-h-[44px] items-center rounded-lg bg-green-600 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-green-200 hover:bg-green-700"
      : "inline-flex min-h-[44px] items-center rounded-lg border-[1.5px] border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50"
  if (href) {
    return (
      <a href={href} className={className}>
        {label}
      </a>
    )
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {label}
    </button>
  )
}
